import {
  Box,
  Button,
  Divider,
  FormLabel,
  MenuItem,
  Select,
  Switch,
  Typography,
} from '@mui/material';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import {
  Position,
  useEdges,
  useReactFlow,
  useUpdateNodeInternals,
} from 'reactflow';
import { useAuthStore } from '../../store/storeAuth';
import { isActiveEdge } from '../../util/findActiveHandle';
import { NodeProps } from '../../util/Types/NodeProps';
import InputHandle from '../UI/InputHandle/InputHandle';
import Node from '../UI/Node/Node';
import { nodeColorBasedOnType } from '../UI/Node/nodeConstants';

import { styled } from 'styled-components';
import { axiosInstance } from '../../service/ApiService';
import useFlowsStore from '../../store/storeFlows';
import { useNotificationStore } from '../../store/storeNotifications';
import { nodeDataAutoSaveDynamic } from '../../util/autosave/nodedata_autosave';
import { modifyData } from '../../util/autosave/changeTypes';
import { checkExecuteTrigger } from '../../util/checkExecute';
import { executeErrorHandle } from '../../util/executeErrorHandle';
import { useDebounceEffect } from '../../util/useDebounceEffect';
import OutputTextarea from '../UI/OutputTextarea/OutputTextarea';
import { TextComponent } from '../UI/PreviewModal/PreviewModal';

const saveTypes = [
  {
    name: 'Save as .txt',
    extension: '.txt',
    fileType: 'txt',
    mimeType: 'text/plain',
  },
  {
    name: 'Save as .html',
    extension: '.html',
    fileType: 'html',
    mimeType: 'text/html',
  },
  {
    name: 'Save as .json',
    extension: '.json',
    fileType: 'json',
    mimeType: 'application/json',
  },
  {
    name: 'Save as .csv',
    extension: '.csv',
    fileType: 'csv',
    mimeType: 'text/csv',
  },
  {
    name: 'Save as .xml',
    extension: '.xml',
    fileType: 'xml',
    mimeType: 'application/xml',
  },
  {
    name: 'Save as .yaml',
    extension: '.yaml',
    fileType: 'yaml',
    mimeType: 'text/yaml',
  },
  {
    name: 'Save as .pdf',
    extension: '.pdf',
    fileType: 'pdf',
    mimeType: 'application/pdf',
  },
  {
    name: 'Save as .docx',
    extension: '.docx',
    fileType: 'docx',
    mimeType:
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  },
  {
    name: 'Save as .png',
    extension: '.png',
    fileType: 'png',
    mimeType: 'image/png',
  },
  {
    name: 'Save as .jpg',
    extension: '.jpg',
    fileType: 'jpg',
    mimeType: 'image/jpeg',
  },
  {
    name: 'Save as .webp',
    extension: '.webp',
    fileType: 'webp',
    mimeType: 'image/webp',
  },
];

const BlueSwitch = styled(Switch)(() => ({
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: 'rgba(54, 80, 222, 1)',
  },
}));

interface FileLinkData {
  src: string;
  name: string;
}

function getInitialSavingTypeByFileLinkName(extension: string | undefined) {
  if (extension) {
    const savedType = saveTypes.find(
      saveType => saveType.extension === extension,
    );

    if (savedType) {
      return savedType.name;
    }
  }

  return saveTypes[0].name;
}

function OutputControllerNode({ data, type, id }: NodeProps) {
  const edges = useEdges();
  const { setSaving, flowId, setSchema } = useFlowsStore(state => state);

  const { setNodes: setNodeState } = useReactFlow();

  const updateNodeInternals = useUpdateNodeInternals();

  const user = useAuthStore(state => state.user);
  const setNotification = useNotificationStore(state => state.setNotification);

  const [isLoading, setIsLoading] = useState(false);
  const [hasToExecute, setHasToExecute] = useState(true);
  const [executeCounter, setExecuteCounter] = useState(0);
  // const [showDataModal, setShowDataModal] = useState(false);
  const [savingType, setSavingType] = useState(
    getInitialSavingTypeByFileLinkName(data?.fileExtension),
  );
  const [size, setSize] = useState({
    height: data?.size?.height || 600,
    width: data?.size?.width || 800,
  });
  const [filter, setFilter] = useState(data.filter || 'all');
  const [saveOutput, setSaveOutput] = useState(data?.saveOutput ?? false);
  const [fileLink, setFileLink] = useState<FileLinkData | undefined>(
    data?.fileLink || undefined,
  );
  const [useDocraptor, setUseDocraptor] = useState(data?.useDocraptor ?? false);

  const debounceTimeoutRef = useRef<any>(null);
  const savingTypeRef = useRef<null | string>(savingType);

  useEffect(() => {
    if (data?.fileLink) {
      setFileLink(data.fileLink);
    }
  }, [data?.fileLink]);

  const onExecute = useCallback(async () => {
    try {
      setIsLoading(true);

      const inputData = data?.textData || [];
      const processedData = inputData.map((item: any) => item.text).join('\n');

      if (processedData.includes('<canvas')) {
        setNotification({
          type: 'warning',
          message:
            "Your content contains <canvas> elements, which aren't currently supported. Try to create your charts using d3.js library in HTML.",
        });
      }

      let fileLinkResponse: FileLinkData | undefined = undefined;

      if (saveOutput) {
        const findType = saveTypes.find(type => type.name === savingType);

        if (findType) {
          let endpoint = '/upload/text';
          if (findType.fileType === 'pdf' && useDocraptor) {
            endpoint = '/upload/text-docraptor';
          }

          const { data: res } = await axiosInstance.post(endpoint, {
            text: processedData || '',
            fileType: findType.fileType,
          });

          fileLinkResponse = res.data as FileLinkData;
          setFileLink(fileLinkResponse);
        }
      }

      nodeDataAutoSaveDynamic({
        newEdges: edges,
        setNodeState,
        id,
        flowId,
        setSaving,
        flowTriggerData: checkExecuteTrigger(data, id),
        changeType: 'execute',
        objectData: {
          text: processedData,
          textData: inputData,
          fileLink: fileLinkResponse,
        },
        objectCallerData: [
          ...(user?.spaceObjects || []),
          ...(user?.objects || []),
        ],
        setSchema,
      });

      if (fileLinkResponse) {
        setTimeout(() => {
          setNodeState((currentNodes: any[]) => {
            const updatedNodes = currentNodes.map(node => {
              if (node.id === id) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    fileLink: fileLinkResponse,
                  },
                };
              }
              return node;
            });

            return modifyData(
              updatedNodes,
              id,
              { fileLink: fileLinkResponse } as any,
              edges,
              'changeText',
              [...(user?.spaceObjects || []), ...(user?.objects || [])],
            );
          });
        }, 100);
      }

      const contentOutEdge = edges?.find(
        edge => edge?.source === id && edge?.sourceHandle === 'contentOut',
      );

      if (contentOutEdge) {
        nodeDataAutoSaveDynamic({
          newEdges: edges,
          setNodeState,
          id: contentOutEdge.target,
          flowId,
          setSaving,
          flowTriggerData: checkExecuteTrigger(data, id),
          changeType: 'execute',
          objectData: {
            text: processedData,
            textData: inputData,
          },
          setSchema,
        });
      }
    } catch (error) {
      executeErrorHandle(
        executeCounter,
        setExecuteCounter,
        setHasToExecute,
        data,
        edges,
        setNodeState,
        setSaving,
        id,
        flowId || '',
        setSchema,
        setNotification,
        error,
        [...(user?.spaceObjects || []), ...(user?.objects || [])],
      );
    } finally {
      setIsLoading(false);
      setHasToExecute(true);
    }
  }, [
    data,
    edges,
    executeCounter,
    flowId,
    id,
    savingType,
    useDocraptor,
    setNodeState,
    setNotification,
    setSaving,
    setSchema,
    user?.objects,
    user?.spaceObjects,
  ]);

  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    if (data?.flowTrigger?.id && !data?.canceled && !data?.paused) {
      if (hasToExecute) {
        debounceTimeoutRef.current = setTimeout(() => {
          setHasToExecute(false);
          onExecute();
        }, 2000);
      }
    }

    return () => {
      clearTimeout(debounceTimeoutRef.current);
    };
  }, [data?.flowTrigger, hasToExecute, data?.canceled, onExecute]);

  const onChangeSavingType = useCallback(
    (e: { target: { value: React.SetStateAction<string> } }) => {
      setSavingType(e.target.value);
      savingTypeRef.current = e.target.value as string;
    },
    [setSavingType],
  );

  const onUseDocraptorChange = () => {
    setUseDocraptor((prev: boolean) => !prev);
    updateNodeInternals(id);

    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      id,
      flowId,
      objectCallerData: user?.objects,
      objectData: {
        useDocraptor: !useDocraptor,
      },
      setSchema,
      setSaving,
    });
  };

  const onSaveOutputChange = async () => {
    setSaveOutput((prev: boolean) => !prev);
    updateNodeInternals(id);

    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      id,
      flowId,
      objectCallerData: user?.objects,
      objectData: {
        saveOutput: !saveOutput,
      },
      setSchema,
      setSaving,
    });

    const findType = saveTypes.find(type => type.name === savingType);

    const response = data?.textData
      ?.map((item: { text: string }) => item.text)
      .join(' \n');

    if (!findType) {
      return;
    }

    if (!response) {
      return;
    }

    let endpoint = '/upload/text';
    if (findType.fileType === 'pdf' && useDocraptor) {
      endpoint = '/upload/text-docraptor';
    }

    const { data: res } = await axiosInstance.post(endpoint, {
      text: response || '',
      fileType: findType.fileType,
    });

    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      id,
      flowId,
      objectCallerData: user?.objects,
      objectData: {
        fileLink: res.data,
      },
      setSchema,
      setSaving,
    });
  };

  const onChangeFilter = (event: {
    target: { value: React.SetStateAction<string> };
  }) => {
    setFilter(event.target.value);
    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      id,
      setSaving,
      changeType: 'changeText',
      flowId,
      objectData: {
        filter: event.target.value,
      },
      setSchema,
    });
  };

  const onChangeInput = useCallback(
    (content: {
      target: { value: string | number; name: string | number };
    }) => {
      setSize(state => ({
        ...state,
        [content.target.name]: Number(content.target.value),
      }));
    },
    [],
  );

  const onCopy = (text: string) => {
    navigator.clipboard.writeText(text);

    setNotification({
      type: 'success',
      message: 'Text copied to clipboard',
    });
  };

  useDebounceEffect(
    () => {
      nodeDataAutoSaveDynamic({
        newEdges: edges,
        setNodeState,
        id,
        setSaving,
        changeType: 'changeText',
        flowId,
        objectData: {
          size: {
            height: size.height,
            width: size.width,
          },
        },
        setSchema,
      });
    },
    [size],
    500,
  );

  useDebounceEffect(
    () => {
      nodeDataAutoSaveDynamic({
        newEdges: edges,
        setNodeState,
        id,
        setSaving,
        changeType: 'changeText',
        flowId,
        objectData: {
          fileExtension: saveTypes.find(type => type.name === savingType)
            ?.extension,
        },
        setSchema,
      });
    },
    [savingType],
    500,
  );

  useDebounceEffect(
    () => {
      nodeDataAutoSaveDynamic({
        newEdges: edges,
        setNodeState,
        id,
        setSaving,
        changeType: 'changeText',
        flowId,
        objectData: {
          useDocraptor: useDocraptor,
        },
        setSchema,
      });
    },
    [useDocraptor],
    500,
  );

  const onDownload = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();

      if (!savingTypeRef.current) return;

      const findType = saveTypes.find(
        type => type.name === savingTypeRef.current,
      );

      if (!findType) {
        return;
      }

      const response = data?.textData
        ?.map((item: { text: string }) => item.text)
        .join(' \n');

      try {
        let endpoint = '/upload/text';
        if (findType?.fileType === 'pdf' && useDocraptor) {
          endpoint = '/upload/text-docraptor';
        }

        const {
          data: { data: fileLinkResponse },
        } = await axiosInstance.post(endpoint, {
          text: response || '',
          fileType: findType?.fileType,
        });

        const { data: res } = await axiosInstance.get('/upload/presigned-url', {
          params: {
            url: fileLinkResponse.src,
          },
        });

        const urlContentBlob = new Blob(
          [new Uint8Array(res.data.file.data).buffer],
          {
            type: findType.mimeType,
          },
        );

        const url = URL.createObjectURL(urlContentBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileLinkResponse.name;
        document.body.appendChild(link);
        link.click();
      } catch (error: any) {
        setNotification({ type: 'error', message: error.message });
      }
    },
    [data?.textData, useDocraptor, setNotification],
  );

  const nodeType =
    nodeColorBasedOnType[type as keyof typeof nodeColorBasedOnType];
  const findColor = nodeType.colors;

  return (
    <Node
      id={id}
      data={data}
      type={type}
      size={size || data?.size}
      btnText="Run"
      onSubmit={onExecute}
      isLoading={isLoading}
      edges={edges}
    >
      <Box
        sx={{
          display: 'grid',
          gap: '0.5rem',
          gridTemplateRows: '3fr min-content min-content',
          height: '100%',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateRows: 'min-content 3fr min-content',
            overflow: 'auto',
          }}
          p="16px"
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '10px',
            }}
          >
            <Box>
              <FormLabel
                sx={{
                  color:
                    user?.theme === 'dark' ? '#FFF' : 'rgba(71, 84, 103, 1)',
                }}
              >
                Save output and generate file link.
              </FormLabel>
              <BlueSwitch
                value={'Monthly'}
                checked={saveOutput}
                onChange={onSaveOutputChange}
                inputProps={{ 'aria-label': 'controlled' }}
              />
            </Box>

            <Select
              value={filter}
              onChange={onChangeFilter}
              className="nodrag"
              size="small"
              sx={{
                '& .MuiList-root': {
                  backgroundColor:
                    user?.theme === 'dark' ? '#101828' : '#F9FAFB',
                },
                '& .MuiSvgIcon-root': {
                  color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                },
                borderRadius: '8px',
                border: 'none',
                boxShadow: 'none',
                '.MuiOutlinedInput-notchedOutline': { border: 0 },
                bgcolor: user?.theme === 'dark' ? '#101828' : '#F9FAFB',
                color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    '& .Mui-selected': {
                      backgroundColor:
                        user?.theme === 'dark' ? '#667085' : '#D0D5DD',
                    },
                    backgroundColor:
                      user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                  },
                },
              }}
            >
              <MenuItem
                value="all"
                sx={{
                  color:
                    user?.theme === 'dark' ? '#FFF' : 'rgba(71, 84, 103, 1)',
                }}
              >
                Intake all
              </MenuItem>
              <MenuItem
                value="code"
                sx={{
                  color:
                    user?.theme === 'dark' ? '#FFF' : 'rgba(71, 84, 103, 1)',
                }}
              >
                Only Intake 'code'
              </MenuItem>
              <MenuItem
                value="table"
                sx={{
                  color:
                    user?.theme === 'dark' ? '#FFF' : 'rgba(71, 84, 103, 1)',
                }}
              >
                Only Intake 'tables'
              </MenuItem>
            </Select>

            <Box>
              <input
                type="number"
                name="width"
                placeholder="Enter Width"
                min={200}
                value={size.width}
                onChange={onChangeInput}
                max={1000}
                className="nodrag"
                style={{
                  width: '60px',
                  border: `1px solid ${
                    user?.theme === 'dark' ? '#344054' : '#D0D5DD'
                  }`,
                  backgroundColor:
                    user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                  color: user?.theme === 'dark' ? '#98A2B3' : 'black',
                }}
              />
              <input
                type="number"
                name="height"
                min={200}
                placeholder="Enter Height"
                max={1000}
                value={size.height}
                onChange={onChangeInput}
                style={{
                  width: '60px',
                  border: `1px solid ${
                    user?.theme === 'dark' ? '#344054' : '#D0D5DD'
                  }`,
                  backgroundColor:
                    user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                  color: user?.theme === 'dark' ? '#98A2B3' : 'black',
                }}
                className="nodrag"
              />
            </Box>
          </Box>

          <Box sx={{ overflow: 'auto' }}>
            {data?.textData?.map((item: any) => (
              <TextComponent
                // size={data?.size}
                key={item?.text?.slice(0, 100) + item.id}
                item={item}
                user={user}
                onCopy={onCopy}
                contentType={filter}
              />
            ))}
          </Box>

          {/* Download and save type */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt={2}
          >
            <Button variant="contained" onClick={onDownload}>
              Download
            </Button>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {savingType === 'Save as .pdf' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormLabel
                    sx={{
                      color:
                        user?.theme === 'dark'
                          ? '#FFF'
                          : 'rgba(71, 84, 103, 1)',
                      fontSize: '0.875rem',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Use DocRaptor
                  </FormLabel>
                  <BlueSwitch
                    value={'Docraptor'}
                    checked={useDocraptor}
                    onChange={onUseDocraptorChange}
                    inputProps={{ 'aria-label': 'docraptor-controlled' }}
                  />
                </Box>
              )}
              <Select
                onChange={onChangeSavingType}
                value={savingType}
                name="saveTypes"
                className="nodrag"
                sx={{
                  height: 'unset',
                  '& .MuiList-root': {
                    backgroundColor:
                      user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                  },
                  bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                  color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                  border: `2px solid ${
                    user?.theme === 'dark' ? '#475467' : '#EAECF0'
                  }`,
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      '& .Mui-selected': {
                        backgroundColor:
                          user?.theme === 'dark' ? '#667085' : '#D0D5DD',
                      },
                      backgroundColor:
                        user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                    },
                  },
                  style: {
                    maxHeight: '700px',
                  },
                }}
              >
                {saveTypes.map((saveType, index) => (
                  <MenuItem key={index} value={saveType.name}>
                    <Typography
                      sx={{
                        color: user?.theme === 'dark' ? '#D0D5DD' : 'black',
                      }}
                    >
                      {saveType.name}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Box>
        </Box>

        <Divider />

        <Box p="16px">
          {saveOutput && (
            <OutputTextarea
              value={fileLink ? fileLink.src : 'No Link generated'}
              activeHandle={isActiveEdge(edges, id, 'fileLink', 'source')}
              labelName="Link"
              handleId="fileLink"
              nodeLabel={data?.label}
              type="source"
              findColor={findColor}
              style={{
                marginBottom: 0,
              }}
            />
          )}
        </Box>
      </Box>
      {/*
      <Box>
        <DataVisualizationModal
          resData={''}
          showDataModal={showDataModal}
          setShowDataModal={setShowDataModal}
        />
      </Box>
         */}

      <InputHandle
        activeColor={findColor?.color}
        handleId="contentIn"
        isActive={isActiveEdge(edges, id, 'contentIn', 'target')}
        left={-12}
        top={'50%'}
        position={Position.Left}
        type="target"
      />

      <InputHandle
        activeColor={findColor?.color}
        handleId="contentOut"
        isActive={isActiveEdge(edges, id, 'contentOut', 'source')}
        right={-12}
        top={'20%'}
        position={Position.Right}
        type="source"
      />
    </Node>
  );
}

export default memo(OutputControllerNode);
